import { useState, useEffect } from "react";
import BotLinkButton from "../../../components/BotLinkButton/BotLinkButton";
import TeacherSearch from "../../../components/TeacherSearch/TeacherSearch";
import * as styled from "./StudentInfo.styled.jsx";
import { useNavigate, useParams } from "react-router-dom";
import { getStudentInfo } from "../../../helpers/getStudentInfo.js";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle.jsx";
import axios from "../../../axios";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled.jsx";
import SelectTeacher from "../../../components/SelectTeacher/SelectTeacher.jsx";
import { FaClock, FaTrash } from "react-icons/fa";
import MaterialsSourceSwitch from "../../../components/MaterialsSourceSwitch/MaterialsSourceSwitch.jsx";
import { courseOf, fetchPlatformCourses } from "../../../helpers/materials/platformLessons.js";
import { isPlatformOnly, isSchoolLang } from "../../../helpers/langs";

// Ті самі ключі, що й LEFT_REASONS у backend/models/outreach.js
const LEFT_REASONS = {
  expensive: "Дорого",
  no_time: "Немає часу",
  life_change: "Переїзд / зміна обставин",
  teacher: "Не підійшов викладач",
  goal_reached: "Досяг цілі",
  other_school: "Інша школа / самостійно",
  other: "Інше",
};

// Що бот надіслав, коли учня додали (botAccess з бекенда)
const BOT_ACCESS = {
  sent: { text: "✅ бот надіслав логін і пароль", color: "#2e7d32" },
  no_password: { text: "☑️ бот надіслав вітання без пароля — доступ передавали вручну", color: "#8a6d00" },
  error: { text: "⚠️ бот не зміг надіслати доступ — передайте логін і пароль вручну", color: "#c0392b" },
  not_found: { text: "бот доступ не надсилав (не знайшли пробного з ботом) — передавали вручну", color: "#8a8399" },
};

const BotAccessLine = ({ access }) => {
  const s = BOT_ACCESS[access.status];
  if (!s) return null;
  const at = access.at ? new Date(access.at).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <p style={{ margin: "4px 0 0", fontSize: "0.85rem", color: s.color }}>
      Доступ при створенні: {s.text}{access.error ? ` (${access.error})` : ""}{at ? ` · ${at}` : ""}
    </p>
  );
};

function StudentInfo() {
  const [student, setStudent] = useState({});
  const [teacherClick, setTeacherClick] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSuccessUpdate, setIsSuccessUpdate] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [newDay, setNewDay] = useState("");
  const [newTime, setNewTime] = useState("");
  const [isPaused, setIsPaused] = useState(false);
  const [reason, setReason] = useState('');
  // Пауза (повернеться) чи «пішов» (з причиною) + коли написати знову
  const [pauseMode, setPauseMode] = useState("pause");
  const [leftReason, setLeftReason] = useState("");
  const [comebackAfter, setComebackAfter] = useState("");

  const { value, id } = useParams();
  const navigate = useNavigate();
  const [platformCourses, setPlatformCourses] = useState([]);
  const [savingSource, setSavingSource] = useState(false);

  useEffect(() => {
    fetchPlatformCourses().then(setPlatformCourses);
  }, []);

  // Джерело матеріалів (Canva / уроки платформи) — той самий запит, що й у вчителя в «Мої учні»
  const updateMaterialsSource = async (materialsSource) => {
    setSavingSource(true);
    try {
      await axios.patch(`/students/${student._id}/${student.teacherId}`, { materialsSource });
      setStudent((prev) => ({ ...prev, materialsSource }));
    } catch (err) {
      console.error(err);
    } finally {
      setSavingSource(false);
    }
  };

  useEffect(() => {
    getStudentInfo(value, id).then(({ student }) => setStudent(student));
  }, [value, id]);

  const handleRemove = (studentId, teacherId) => {
    axios
      .delete(`students/${studentId}/${teacherId}`)
      .then(() => {
        navigate("/admin/ourstudents");
      })
      .catch((err) => console.error(err));
  };

  const updateTeacher = (newTeacherId, newTeacherName) => {
    setSelectedTeacher(newTeacherName);
    axios
      .put(`update-teacher/${student._id}/${student.teacherId}/${newTeacherId}`)
      .then(() => {
        setIsSuccessUpdate(true);
      })
      .catch((err) => {
        setIsSuccessUpdate(false);
        console.log(err);
      })
      .finally(() => {
        setTeacherClick(false);
      });
  };

  const handleTimeChange = async (newTime) => {
    if (editingSchedule) {
      console.log(`Updating time for ${editingSchedule.day} to ${newTime}`);

      try {
        const response = await axios.post("student/edit-lesson-time", {
          studentId: student._id,
          day: editingSchedule.day,
          newTime,
        });

        if (response.status === 200) {
          console.log("Time updated successfully on the server", response.data);
          const updatedSchedule = student.classSchedule.map((item) =>
            item.day === editingSchedule.day ? { ...item, time: newTime } : item
          );
          setStudent({ ...student, classSchedule: updatedSchedule });
        } else {
          console.error("Failed to update time", response.data);
        }
      } catch (error) {
        console.error("Error updating time on the server", error);
      } finally {
        setEditingSchedule(null);
      }
    }
  };

  const cancelEditing = () => {
    setEditingSchedule(null);
  };

  const removeDay = (day, time) => {
    axios
      .post("/student/remove-day", {
        studentId: student._id,
        day: day,
        time: time,
      })
      .then(({data}) => {
        setStudent({ ...student, classSchedule: data.updatedSchedule });
      })
      .catch((err) => console.error(err));
  };

  const addNewDayAndTime = async () => {
    if (!newDay || !newTime) {
      alert("Оберіть день та час");
      return;
    }

    try {
      const response = await axios.post("student/add-schedule", {
        studentId: student._id,
        day: newDay,
        time: newTime,
      });

      if (response.status === 200) {
        console.log("Добавлено успешно", response.data);
        setStudent({
          ...student,
          classSchedule: response.data.updatedSchedule,
        });
        setNewDay("");
        setNewTime("");
      } else {
        console.error("Ошибка при добавлении:", response.data);
      }
    } catch (error) {
      console.error("Ошибка добавления дня и времени:", error);
    }
  };
  
  // Тестовий акаунт школи не рахується у воронці, продовженнях і ризику відтоку
  const toggleTest = async (isTest) => {
    try {
      const { data } = await axios.patch(`admin/students/${student._id}/test`, { isTest });
      setStudent((prev) => ({ ...prev, isTest: data.isTest }));
    } catch (error) {
      console.error("Не вдалося оновити прапорець тестового акаунта", error);
    }
  };

  // Окрема ставка вчителю за урок з цим учнем (замість таблиці вчителя); діє, поки учень у цього вчителя
  const [rateInput, setRateInput] = useState("");
  const [rateSaved, setRateSaved] = useState(false);
  const rateActive = student.teacherRate?.value && student.teacherRate.teacherId === student.teacherId;
  useEffect(() => {
    setRateInput(rateActive ? String(student.teacherRate.value) : "");
  }, [rateActive, student.teacherRate?.value]);
  const saveTeacherRate = async () => {
    try {
      const { data } = await axios.patch(`admin/students/${student._id}/teacher-rate`, { value: Number(rateInput) || 0 });
      setStudent((prev) => ({ ...prev, teacherRate: data.teacherRate }));
      setRateSaved(true);
      setTimeout(() => setRateSaved(false), 2000);
    } catch (error) {
      window.alert(error?.response?.data?.message || "Не вдалося зберегти ставку");
    }
  };

  // Відвʼязати Telegram (напр., менеджер сам відкрив посилання учня). Останній чат → бонус +3 ⭐ знімається
  const unlinkChat = async (c) => {
    if (!window.confirm(`Відвʼязати ${c.username ? "@" + c.username : "цей Telegram"} від учня? Бот більше не надсилатиме йому сповіщення цього учня.`)) return;
    try {
      const { data } = await axios.delete(`admin/students/${student._id}/bot-chats/${c.chatId}`);
      setStudent((prev) => ({ ...prev, botChats: data.botChats, ...(data.stars !== undefined ? { stars: data.stars } : {}) }));
    } catch (error) {
      window.alert("Не вдалося відвʼязати Telegram");
    }
  };

  const togglePauseStatus = async () => {
    console.log(student.teacherId)
    try {
      const left = !student.isPaused && pauseMode === "left";
      if (left && !leftReason) return alert("Оберіть, чому учень пішов");
      const response = await axios.put(`student/pause/${student._id}`, {
        isPaused: !student.isPaused,
        pauseNote: reason, 
        teacherId: student.teacherId,
        left,
        leftReason: left ? leftReason : undefined,
        comebackAfter: comebackAfter || undefined,
      });
      setStudent(response.data); 
      setReason("")
      setIsPaused(false)
      setPauseMode("pause"); setLeftReason(""); setComebackAfter("");
    } catch (error) {
      console.error("Не вдалося оновити статус паузи", error);
      alert(error?.response?.data?.message || "Не вдалося оновити статус паузи");
    }
  };
  return (
    <PagesWrapper>
      <TopPageTitle value={student.name} />
      <styled.List>
        <styled.Item>
          <styled.ItemTitle>
            Ім’я: {student.name} - id:{student.studentId}
          </styled.ItemTitle>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 14, cursor: "pointer" }}>
            <input type="checkbox" style={{ appearance: "auto", WebkitAppearance: "auto" }} checked={Boolean(student.isTest)} onChange={(e) => toggleTest(e.target.checked)} />
            Тестовий акаунт (не рахується в аналітиці)
          </label>
        </styled.Item>
        <styled.Item>
          <styled.ItemTitle>Група: {student.ageGroup}</styled.ItemTitle>
        </styled.Item>
        <styled.Item>
          <styled.ItemTitle>Ставка вчителю за урок з цим учнем</styled.ItemTitle>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 14, flexWrap: "wrap" }}>
            <input type="number" min="0" max="1000" placeholder="за таблицею вчителя" value={rateInput} onChange={(e) => setRateInput(e.target.value)} style={{ width: 170, padding: "4px 8px" }} />
            <span>грн</span>
            <button type="button" onClick={saveTeacherRate}>Зберегти</button>
            {rateSaved && <span>✓ збережено</span>}
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
            Порожньо — як у таблиці вчителя. Діє лише поки учень у поточного вчителя.
          </div>
        </styled.Item>
        <styled.Item>
          <styled.ItemTitle>Рівень: {student.englishLevel}</styled.ItemTitle>
        </styled.Item>
        {isSchoolLang(student.accessType) && !isPlatformOnly(student.accessType) && (
          <styled.Item>
            <MaterialsSourceSwitch
              value={student.materialsSource}
              hasPlatform={platformCourses.includes(courseOf(student.ageGroup, student.englishLevel, student.accessType))}
              disabled={savingSource}
              onChange={updateMaterialsSource}
            />
          </styled.Item>
        )}
        <styled.Item>
          <styled.EditWrapper>
            <styled.ItemTitle>
              Викладач:{" "}
              {selectedTeacher.length <= 0
                ? student.teacherName
                : selectedTeacher}
            </styled.ItemTitle>
            <styled.Pen onClick={() => setTeacherClick((prev) => !prev)} />
          </styled.EditWrapper>
          {teacherClick && (
            <styled.SelectWrapper>
              <SelectTeacher
                setLoading={setLoading}
                loading={loading}
                setTeachers={setTeachers}
                teachers={teachers}
                setTeacherClick={setTeacherClick}
                teacherClick={teacherClick}
                handleClick={updateTeacher}
                selectedTeacher={selectedTeacher}
                title={"Виберіть нового вчителя"}
              />
            </styled.SelectWrapper>
          )}
          {isSuccessUpdate && (
            <p style={{ color: "green" }}>Вчителя успішно оновленно</p>
          )}
          {isSuccessUpdate === false && (
            <p style={{ color: "red" }}>Щось пішло не так ....</p>
          )}
          {student._id && <TeacherSearch
            student={student}
            onMoved={(teacherId, teacherName) => {
              setSelectedTeacher(teacherName);
              setStudent((s) => ({ ...s, teacherId, teacherName }));
            }}
          />}
        </styled.Item>

        <styled.Item>
          <styled.ItemTitle>
            Номер телефону: {student.phoneNumber}
          </styled.ItemTitle>
        </styled.Item>
        <styled.Item>
          <styled.ItemTitle style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            Telegram-бот:{" "}
            {student.botChats?.length
              ? student.botChats.map((c) => (
                  <span key={c.chatId} style={{ color: c.blocked ? "#c0392b" : undefined, display: "block", flexBasis: "100%" }}>
                    {c.username ? `@${c.username}` : "Telegram"} {c.blocked ? "— заблокував бота" : "✅"}{c.promoOff ? " · без акцій" : ""}
                    <button type="button" onClick={() => unlinkChat(c)} title="Відвʼязати цей Telegram від учня"
                      style={{ marginLeft: 8, border: "1px solid #dcd7ee", background: "#fff", borderRadius: 8, padding: "2px 8px", cursor: "pointer", font: "inherit", fontSize: "0.78rem" }}>
                      відвʼязати
                    </button>
                  </span>
                ))
              : <span style={{ color: "#8a8399" }}>не підключено</span>}
            <BotLinkButton kind="student" id={student._id}
              hint="Скопіювати особисте посилання для учня (можна підключити кілька Telegram, наприклад, учня і батьків)" />
          </styled.ItemTitle>
          {student.botAccess?.status && <BotAccessLine access={student.botAccess} />}
        </styled.Item>
        <styled.Item>
          <styled.ItemTitle>
            Пакет уроків: {student.lessonPackage}
          </styled.ItemTitle>
        </styled.Item>
        <styled.Item>
          <styled.ItemTitle>Розклад: </styled.ItemTitle>
          {student?.classSchedule?.map(({ day, time }) => (
            <styled.ScheduleWrapper key={day}>
              {editingSchedule?.day === day ? (
                <div style={{ marginBottom: "15px" }}>
                  <input
                    type="time"
                    value={editingSchedule.time}
                    onChange={(e) =>
                      setEditingSchedule({
                        ...editingSchedule,
                        time: e.target.value,
                      })
                    }
                    style={{ marginRight: "15px" }}
                  />
                  <button
                    onClick={() => handleTimeChange(editingSchedule.time)}
                    style={{ marginRight: "15px" }}
                  >
                    Зберегти
                  </button>
                  <button onClick={cancelEditing}>Скасувати</button>
                </div>
              ) : (
                <>
                  <styled.ItemTitle>
                    {day}: {time}
                  </styled.ItemTitle>
                  <styled.EditIconsButton
                    onClick={() => setEditingSchedule({ day, time })}
                  >
                    <FaClock />
                  </styled.EditIconsButton>
                  <styled.EditIconsButton onClick={() => removeDay(day, time)}>
                    <FaTrash />
                  </styled.EditIconsButton>
                </>
              )}
            </styled.ScheduleWrapper>
          ))}
        </styled.Item>
        <styled.Item>
          <styled.ItemTitle>Додати новий день:</styled.ItemTitle>
          <styled.AddScheduleWrapper style={{ display: "flex", alignItems: "center" }}>
            <styled.Select
              value={newDay}
              onChange={(e) => setNewDay(e.target.value)}
              style={{ marginRight: "15px" }}
            >
              <option value="" >Оберіть день</option>
              <option value="monday">Понеділок</option>
              <option value="tuesday">Вівторок</option>
              <option value="wednesday">Середа</option>
              <option value="thursday">Четвер</option>
              <option value="friday">П'ятниця</option>
              <option value="saturday">Субота</option>
              <option value="sunday">Неділя</option>
            </styled.Select>
            <styled.TimeInput
              type="time"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
              style={{ marginRight: "15px" }}
            />
            <styled.AddButton onClick={addNewDayAndTime}>Додати</styled.AddButton>
          </styled.AddScheduleWrapper>
        </styled.Item>
        <styled.ButtonsWrapper>
          <styled.PauseWrapper>
             <styled.PauseText onClick={() => setIsPaused(prev => !prev)}>{student.isPaused ? "Зняти з паузи?" : "Поставити на паузу або позначити «пішов»?"}</styled.PauseText>
             {isPaused && <>
               {!student.isPaused && (
                 <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "8px 0", fontSize: 14 }}>
                   <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                     <input type="radio" style={{ appearance: "auto", WebkitAppearance: "auto" }} checked={pauseMode === "pause"} onChange={() => setPauseMode("pause")} /> Пауза - планує повернутися (нагадаємо написати через 30 днів)
                   </label>
                   <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                     <input type="radio" style={{ appearance: "auto", WebkitAppearance: "auto" }} checked={pauseMode === "left"} onChange={() => setPauseMode("left")} /> Пішов (нагадаємо спробувати повернути через 60 днів)
                   </label>
                   {pauseMode === "left" && (
                     <select value={leftReason} onChange={(e) => setLeftReason(e.target.value)} style={{ padding: 6, maxWidth: 320 }}>
                       <option value="" disabled>Чому пішов?</option>
                       {Object.entries(LEFT_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                     </select>
                   )}
                   <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                     Написати знову після: <input type="date" value={comebackAfter} onChange={(e) => setComebackAfter(e.target.value)} /> <span style={{ opacity: 0.6 }}>(необовʼязково)</span>
                   </label>
                 </div>
               )}
               <styled.PauseInput placeholder={student.isPaused ? "Коментар (необовʼязково)" : "Нотатка: що сказав учень"} onChange={(e) => setReason(e.target.value)}/>
                <styled.RemoveBtn isPaused onClick={() => togglePauseStatus()}>Підтвердити</styled.RemoveBtn>
             </>}
           
          </styled.PauseWrapper>
         
          <styled.RemoveBtn
            onClick={() => handleRemove(student._id, student.teacherId)}
          >
            Видалити учня
          </styled.RemoveBtn>
        </styled.ButtonsWrapper>
      </styled.List>
    </PagesWrapper>
  );
}

export default StudentInfo;
